import * as React from 'react';
import classNames from 'classnames';
import { SearchBar } from '@quality24/design-system';

import { SearchFilter, SearchFilterComponentData } from '../../../../@types';
import { useQueryFilter } from '../../../../hooks/filterHooks';
import {
  parseFilterData,
  formatFilterData,
  formatSearchFilterId,
} from '../../../../utils/filterUtils';

export interface Props {
  className?: string;
  style?: React.CSSProperties;
  // loading?: boolean;
  partitionId?: 'default' | string;
  config: SearchFilterComponentData;
}

/**
 * <SearchFilterContainer> page
 */
export const SearchFilterContainer: React.FunctionComponent<Props> = ({
  className,
  style,
  partitionId = 'default',
  config: { id, className: configClassName, placeholder, fields },
}) => {
  const filterId = React.useMemo(() => formatSearchFilterId(id), [id]);
  const { filters, upsertFilter, removeFilter } = useQueryFilter(
    [filterId],
    partitionId,
  );

  // Parse query filter format
  const value = React.useMemo(() => {
    if (
      !filters ||
      !filters[filterId] ||
      !(filters[filterId] as SearchFilter).or
    ) {
      return '';
    }

    const [field] = fields;
    const [filter] = (filters[filterId] as SearchFilter).or;
    return filter
      ? parseFilterData(
          field.parser,
          filter[field.id],
          field.parserOptions
            ? { parserOptions: field.parserOptions }
            : undefined,
        )
      : '';
  }, [filters, filterId, fields]);

  /**
   * Handles the filter change, saving it the the context
   */
  const handleFilterChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      (event) => {
        if (event.target.value === '') {
          removeFilter(filterId);
          return;
        }

        upsertFilter(filterId, {
          or: fields
            .filter((field) => {
              if (field.parser === 'uuid-search') {
                const uuidV4regex =
                  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
                return uuidV4regex.test(event.target.value);
              }
              return true;
            })
            .map((field) => ({
              [field.id]: formatFilterData(
                field.parser,
                event.target.value,
                field.parserOptions
                  ? { parserOptions: field.parserOptions }
                  : undefined,
              ),
            })),
        } as SearchFilter);
      },
      [upsertFilter, removeFilter, filterId, fields],
    );

  // Add log for debuging with DVC-Designer
  if (process.env.REACT_APP_HOST_PROJECT === 'designer') {
    // eslint-disable-next-line no-console
    console.info('SearchFilterContainer', {
      filterContext: filters,
    });
  }

  return (
    <SearchBar
      className={classNames(className, configClassName)}
      style={style}
      name={filterId}
      placeholder={placeholder}
      defaultValue={value}
      padding="sm"
      // loading={loading}
      onChange={handleFilterChange}
      debounce
      debounceInterval={1000}
    />
  );
};

export default SearchFilterContainer;
